import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useDatabaseContent } from '@databases/database-state';
import {
  DRAG_HANDLER_TD_ELEMENT_WIDTH,
  VERT_DRAG_HANDLER_ICON_WIDTH,
} from '@databases/icons';
import Z_INDEX from '@databases/layout/z-index';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { PresenceNameplate, usePresence } from '@databases/presence';
import { defaultStoreUtils } from '@databases/redux';
import { selectIsHoverDeleteEntriesButton } from '@databases/redux/cellSelections/selectors';
import {
  selectedBorder,
  selectedBorderHoverDelete,
  visualRefreshSelectBorder,
} from '@databases/styles';
import { NUMBERED_COLUMN_WIDTH, TABLE_CLASSNAMES } from '@databases/utils/constants';
import useContainerScrollPosition from '@databases/utils/hooks/useContainerScrollPosition';
import useView from '@databases/views/useView';
import classNames from 'classnames';
import React, { type FunctionComponent, useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { shouldShowEntryPrefix } from '../../../utils';

import EntryCheckbox from './EntryCheckbox';
import useHasNumberedEntries from './UseHasNumberedEntries';

const FirstCellContentContainer = xcss({
  height: token('space.250', '20px'),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

export const StyledEntryPrefixTd = styled.td<{
  hasNumberedEntries: boolean;
  isVisualRefreshM1Enabled: boolean;
  isSelected: boolean;
  dragHandlerTdElementWidth: number;
  isFirstEntry: boolean;
  isLastEntry: boolean;
}>`
  position: relative;
  width: ${(props) => (props.hasNumberedEntries ? NUMBERED_COLUMN_WIDTH : 0)}px;
  left: ${(props) => props.dragHandlerTdElementWidth / 2}px;

  visibility: ${(props) => (props.hasNumberedEntries ? 'visible' : 'hidden')};
  justify-content: start;
  align-items: center;
  background: ${token('elevation.surface', '#FFFFF')};
  z-index: ${Z_INDEX.TABLE_NUMBERED_COLUMN};
  color: ${token('color.text.subtlest', '#6B778C')};

  // Without this entries will grow larger due to targeted css from TableLaout.
  ${({ hasNumberedEntries }) =>
    !hasNumberedEntries &&
    css`
      height: 0;
    `}

  ${({ isSelected }) =>
    isSelected &&
    css`
      background: ${token('color.background.accent.blue.subtlest', '#E9F2FF')};

      &.${TABLE_CLASSNAMES.IS_HOVER_DELETE_BUTTON} {
        && {
          transition: background-color 0.2s;
          background: ${token('color.background.danger.hovered', '#FFD5D2')};
        }
      }
    `}
  ${({ isVisualRefreshM1Enabled, isFirstEntry, isLastEntry }) =>
    isVisualRefreshM1Enabled &&
    css`
      :hover {
        border-radius: ${isFirstEntry ? token('radius.large', '8px') : '0px'} 0 0
          ${isLastEntry ? token('radius.large', '8px') : '0px'};
      }
    `}
`;

export const EntryPrefixShadow = styled.div`
  display: none;
  position: absolute;
  height: 100%;
  width: 2px;
  left: ${NUMBERED_COLUMN_WIDTH - 3}px;
  top: 0px;
  box-shadow: ${token('elevation.shadow.overflow')};
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const EntryPrefixBorder = styled.div<{
  entryBeforeIsSelected: boolean;
  entryAfterIsSelected: boolean;
  isFirstEntry: boolean;
  isLastEntry: boolean;
  isVisualRefreshM1Enabled: boolean;
  borderStyle: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border: ${(props) => props.borderStyle};
  border-top: ${(props) => (props.entryBeforeIsSelected ? 'none' : props.borderStyle)};
  border-bottom: ${(props) => (props.entryAfterIsSelected ? 'none' : props.borderStyle)};
  ${(props) =>
    props.isVisualRefreshM1Enabled &&
    ` border-right: none;
      border-start-start-radius: ${!props.entryBeforeIsSelected ? '3px' : '0'};
      border-end-start-radius: ${!props.entryAfterIsSelected ? '3px' : '0'};
    `}

  margin-top: ${(props) => (props.isFirstEntry ? '0px' : '-1px')};
  margin-left: -1px;
  margin-bottom: ${(props) => (props.isLastEntry ? '0px' : '-1px')};
  margin-right: ${token('space.negative.025', '-2px')};
`;

export type EntryIsSelectedMap = Record<string, boolean>;

export interface EntryPrefixProps {
  contentId: string;
  entryId: string;
  entryIsSelectedMap: EntryIsSelectedMap;
  index: number;
  isFirstEntry: boolean;
  isLastEntry: boolean;
  isSelected: boolean;
  setEntryHover: (isHovered: boolean) => void;
  isReadOnly: boolean;
}

const EntryPrefix: FunctionComponent<EntryPrefixProps> = ({
  contentId,
  entryId,
  entryIsSelectedMap,
  index,
  isFirstEntry,
  isLastEntry,
  isSelected,
  setEntryHover,
  isReadOnly,
}) => {
  const { referencing } = useDatabaseContent();
  const isHoverDeleteEntriesButton = defaultStoreUtils.useEqualSelector(
    (state) => isSelected && selectIsHoverDeleteEntriesButton(state)
  );
  const { view } = useView();
  const showEntryPrefix = shouldShowEntryPrefix(view);
  const hasNumberedEntries = useHasNumberedEntries({ contentId }) && showEntryPrefix;
  const [isHovered, setIsHovered] = useState(false);

  const containerScrollPosition = useContainerScrollPosition(
    'scrollable-inner-container'
  );
  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );
  const dragHandlerTdElementWidth = isVisualRefreshM1Enabled
    ? DRAG_HANDLER_TD_ELEMENT_WIDTH
    : VERT_DRAG_HANDLER_ICON_WIDTH;

  const getEntrySelectionStatus = useCallback(() => {
    const entryIds = Object.keys(entryIsSelectedMap);
    const entryIndex = entryIds.indexOf(entryId);
    if (entryIndex === -1) {
      return { entryBeforeIsSelected: false, entryAfterIsSelected: false };
    }
    const entryBeforeId = entryIds[entryIndex - 1];
    const entryAfterId = entryIds[entryIndex + 1];
    return {
      entryBeforeIsSelected: entryIsSelectedMap[entryBeforeId] ?? false,
      entryAfterIsSelected: entryIsSelectedMap[entryAfterId] ?? false,
    };
  }, [entryId, entryIsSelectedMap]);

  const { entryBeforeIsSelected, entryAfterIsSelected } = getEntrySelectionStatus();

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    setEntryHover(true);
  }, [setEntryHover]);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setEntryHover(false);
  }, [setEntryHover]);

  const borderStyle = isHoverDeleteEntriesButton
    ? selectedBorderHoverDelete
    : isVisualRefreshM1Enabled
    ? visualRefreshSelectBorder
    : selectedBorder;

  const isDatabasePresenceEnabled = getExperimentValue('databasePresence', 'enabled', {
    fireExperimentExposure: false,
  });

  const shouldShowTelepointers = !isSelected && !isReadOnly && isDatabasePresenceEnabled;

  const { participants } = usePresence({
    grouping: { entryId },
    componentId: 'EntryPrefix',
    isEnabled: shouldShowTelepointers,
  });

  //Used to determine whether we render the checkbox or the number in the first column
  const shouldRenderNumber = view.settings.layout.showNumberedEntries;
  const shouldRenderCheckbox =
    isPluginEnabled(PluginKey.CELL_SELECTION) &&
    isVisualRefreshM1Enabled &&
    (isHovered || isSelected || !shouldRenderNumber);

  const nameplateRef = useRef<HTMLTableCellElement>(null);

  return (
    <StyledEntryPrefixTd
      className={classNames('entry-number-table-data', {
        [TABLE_CLASSNAMES.IS_SELECTED]: isSelected,
        [TABLE_CLASSNAMES.IS_HOVER_DELETE_BUTTON]: isHoverDeleteEntriesButton,
      })}
      hasNumberedEntries={hasNumberedEntries}
      isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
      isSelected={isSelected}
      dragHandlerTdElementWidth={dragHandlerTdElementWidth}
      isFirstEntry={isFirstEntry}
      isLastEntry={isLastEntry}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={nameplateRef}
    >
      {shouldShowTelepointers && isHovered && (
        <PresenceNameplate participants={participants} anchorRef={nameplateRef} />
      )}
      {isSelected && (
        <EntryPrefixBorder
          className="entry-number-table-border"
          entryBeforeIsSelected={entryBeforeIsSelected}
          entryAfterIsSelected={entryAfterIsSelected}
          isFirstEntry={isFirstEntry}
          isLastEntry={isLastEntry}
          isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
          borderStyle={borderStyle}
        />
      )}
      <Box xcss={FirstCellContentContainer}>
        {shouldRenderCheckbox ? (
          <EntryCheckbox
            contentId={contentId}
            entryId={entryId}
            isSelected={isSelected}
            referencing={referencing}
            index={index}
          />
        ) : (
          index + 1
        )}
      </Box>

      {isVisualRefreshM1Enabled ? null : (
        <EntryPrefixShadow
          style={{ display: containerScrollPosition > 0 ? 'block' : 'none' }}
        />
      )}
    </StyledEntryPrefixTd>
  );
};

export default EntryPrefix;
